// Stage movement/resizing in the overlay. Commit only keys changed by the gesture.
const EDIT_KEYS = ["x", "y", "width", "height", "fontSize", "fontSizes"];
const TEXT_RECT_KEYS = ["X", "Y", "W", "H"];
const sameValue = (a, b) =>
  Object.is(a, b) ||
  (Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((value, index) => Object.is(value, b[index])));
export const createWidgetMovePreview = () => {
  let session = null;
  return {
    read(file, gestureKey, mode = "geometry") {
      if (
        !session ||
        session.gestureKey !== gestureKey ||
        session.mode !== mode
      ) {
        const temp = file.document.tempFields;
        const names = new Set([
          temp.selectedFormField?.name,
          ...(temp.selectedExtraFields || []).map((field) => field.name),
        ]);
        const insertedText =
          mode === "geometry" && gestureKey?.insertedTextEditId
            ? temp.pdfTextEdits?.find(
                (edit) =>
                  edit.edit_id === gestureKey.insertedTextEditId &&
                  edit.isEditorAdded === true &&
                  edit.kind === "inserted" &&
                  edit.action !== "delete" &&
                  edit.target_item_id === temp.selectedPdfTextItem?.id,
              )
            : null;
        session = {
          mode,
          gestureKey,
          file,
          document: temp.currentDocument,
          originalInsertedText: insertedText?.rect
            ? { ...insertedText, rect: { ...insertedText.rect } }
            : null,
          originals: new Map(
            temp.formFields
              .filter((field) => names.has(field.name))
              .map((field) => [
                field.name,
                Object.fromEntries(EDIT_KEYS.map((key) => [key, field[key]])),
              ]),
          ),
        };
      }
      return session.file;
    },
    update(updater) {
      if (!session) return null;
      session.file =
        typeof updater === "function" ? updater(session.file) : updater;
      return session.file;
    },
    commit(setFile) {
      if (!session) return;
      const completed = session;
      session = null;
      if (completed.mode === "selection") {
        const draft = completed.file.document.tempFields;
        const selectedNames = (draft.selectedExtraFields || []).map(
          (field) => field.name,
        );
        const primaryName = draft.selectedFormField?.name;

        setFile((previous) => {
          const temp = previous?.document?.tempFields;

          if (!temp || temp.currentDocument !== completed.document) {
            return previous;
          }

          // Use current field objects to preserve their latest data.
          const fieldsByName = new Map(
            (temp.formFields || []).map((field) => [field.name, field]),
          );

          const selectedExtraFields = selectedNames
            .map((name) => fieldsByName.get(name))
            .filter(Boolean);

          const selectedFormField = fieldsByName.get(primaryName) || null;
          const previousSelection = temp.selectedExtraFields || [];

          if (
            selectedFormField === temp.selectedFormField &&
            previousSelection.length === selectedExtraFields.length &&
            previousSelection.every(
              (field, index) => field === selectedExtraFields[index],
            )
          ) {
            return previous;
          }

          return {
            ...previous,
            document: {
              ...previous.document,
              tempFields: {
                ...temp,
                selectedFormField,
                selectedExtraFields,
              },
            },
          };
        });

        return;
      }
      if (completed.gestureKey?.insertedTextEditId) {
        const original = completed.originalInsertedText;
        if (!original) return;
        const draft = completed.file.document.tempFields.pdfTextEdits?.find(
          (edit) =>
            edit.edit_id === original.edit_id &&
            edit.target_item_id === original.target_item_id &&
            edit.isEditorAdded === true &&
            edit.kind === "inserted" &&
            edit.action !== "delete" &&
            Number(edit.page) === Number(original.page),
        );
        if (!draft?.rect || draft.rect.W <= 0 || draft.rect.H <= 0) return;

        const geometryPatch = {};
        // Movement changes X/Y; resize handles can change all four values.
        for (const key of TEXT_RECT_KEYS) {
          if (!Number.isFinite(draft.rect[key])) return;
          if (!sameValue(draft.rect[key], original.rect[key])) {
            geometryPatch[key] = draft.rect[key];
          }
        }
        // A click or a return to the original rectangle has nothing to commit.
        if (!Object.keys(geometryPatch).length) return;

        setFile((previous) => {
          const temp = previous?.document?.tempFields;
          if (
            !temp ||
            temp.currentDocument !== completed.document ||
            temp.selectedPdfTextItem?.id !== original.target_item_id ||
            temp.editingInsertedTextId
          ) {
            return previous;
          }
          const current = temp.pdfTextEdits?.find(
            (edit) =>
              edit.edit_id === original.edit_id &&
              edit.target_item_id === original.target_item_id &&
              edit.isEditorAdded === true &&
              edit.kind === "inserted" &&
              edit.action !== "delete" &&
              edit.action === original.action &&
              Number(edit.page) === Number(original.page),
          );
          // Do not overwrite deletion, undo, or an external geometry change.
          if (
            !current?.rect ||
            TEXT_RECT_KEYS.some(
              (key) => !sameValue(current.rect[key], original.rect[key]),
            )
          ) {
            return previous;
          }

          // Merge geometry into the latest record, not the preview's old text
          // or settings. Unrelated records and form fields remain untouched.
          const rect = { ...current.rect, ...geometryPatch };
          return {
            ...previous,
            document: {
              ...previous.document,
              tempFields: {
                ...temp,
                pdfTextEdits: temp.pdfTextEdits.map((edit) =>
                  edit === current ? { ...current, rect } : edit,
                ),
                selectedPdfTextItem: { ...temp.selectedPdfTextItem, rect },
              },
            },
          };
        });
        return;
      }
      const positions = new Map();
      for (const field of completed.file.document.tempFields.formFields) {
        const original = completed.originals.get(field.name);
        if (!original) continue;
        const patch = {};
        for (const key of EDIT_KEYS) {
          if (!sameValue(field[key], original[key])) patch[key] = field[key];
        }
        if (Object.keys(patch).length) positions.set(field.name, patch);
      }
      if (!positions.size) return;

      setFile((previous) => {
        const temp = previous?.document?.tempFields;
        // Never apply an old drag to a different page/document/viewport.
        if (!temp || temp.currentDocument !== completed.document)
          return previous;
        const currentByName = new Map(
          temp.formFields.map((field) => [field.name, field]),
        );
        // Preserve an external geometry change (for example, undo during a drag).
        for (const [name] of positions) {
          const current = currentByName.get(name);
          const original = completed.originals.get(name);
          if (
            current &&
            EDIT_KEYS.some((key) => !sameValue(current[key], original[key]))
          )
            return previous;
        }
        const changedByName = new Map();
        const formFields = temp.formFields.map((field) => {
          const position = positions.get(field.name);
          if (!position) return field;
          const updated = { ...field, ...position };
          changedByName.set(field.name, updated);
          return updated;
        });
        if (!changedByName.size) return previous;
        return {
          ...previous,
          document: {
            ...previous.document,
            tempFields: {
              ...temp,
              formFields,
              selectedFormField:
                changedByName.get(temp.selectedFormField?.name) ||
                temp.selectedFormField,
              selectedExtraFields: (temp.selectedExtraFields || []).map(
                (field) => changedByName.get(field.name) || field,
              ),
            },
          },
        };
      });
    },
    cancel() {
      const wasActive = session !== null;
      session = null;
      return wasActive;
    },
  };
};
